import { Body, Equator, Horizon, MoonPhase, Observer, SearchMoonPhase, SearchRiseSet } from "astronomy-engine";
import { constellationLines, skyStars } from "../data/skyCatalog";

const tokyoObserver = new Observer(35.681236, 139.767125, 40);
const synodicMonth = 29.530588853;

export type ProjectedSkyObject = {
  id: string;
  name: string;
  kind: "star" | "moon" | "planet";
  constellation?: string;
  x: number;
  y: number;
  altitude: number;
  azimuth: number;
  mag: number;
};

export type ProjectedConstellation = {
  id: string;
  name: string;
  points: Array<{ x: number; y: number }>;
};

export type ProjectedCardinal = {
  id: string;
  label: string;
  x: number;
  y: number;
  azimuth: number;
};

export type SkyScene = {
  date: Date;
  stars: ProjectedSkyObject[];
  bodies: ProjectedSkyObject[];
  constellations: ProjectedConstellation[];
  cardinals: ProjectedCardinal[];
  moonPhase: number;
  moonAge: number;
  sunAltitude: number;
  centerAzimuth: number;
};

export type AstroSummary = {
  date: string;
  sunrise: string;
  sunset: string;
  moonrise: string;
  moonset: string;
  moonAge: string;
  daysToFull: string;
  daysToNew: string;
};

const fmtDate = new Intl.DateTimeFormat("ja-JP", {
  timeZone: "Asia/Tokyo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
});

const fmtTime = new Intl.DateTimeFormat("ja-JP", {
  timeZone: "Asia/Tokyo",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false
});

const fmtInput = new Intl.DateTimeFormat("sv-SE", {
  timeZone: "Asia/Tokyo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
});

export const formatJstDate = (date: Date) => fmtDate.format(date).replace(/\//g, "/");

export const formatJstTime = (date: Date) => fmtTime.format(date);

export const toDateInputValue = (date: Date) => fmtInput.format(date);

export const makeSkyDate = (dateValue: string, sliderMinutes: number) => {
  const safeDate = dateValue || toDateInputValue(new Date());
  const hour = 18 + Math.floor(sliderMinutes / 60);
  const minute = sliderMinutes % 60;
  const dayOffset = hour >= 24 ? 1 : 0;
  const hourOfDay = hour % 24;
  const base = new Date(`${safeDate}T${String(hourOfDay).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00+09:00`);
  if (dayOffset) base.setUTCDate(base.getUTCDate() + dayOffset);
  return base;
};

export const describeSliderTime = (sliderMinutes: number) => {
  const hour = (18 + Math.floor(sliderMinutes / 60)) % 24;
  const minute = sliderMinutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const skyHorizontalFov = 180;

const normalizeAzimuth = (value: number) => ((value % 360) + 360) % 360;

const signedAzimuthDelta = (azimuth: number, centerAzimuth: number) => {
  const delta = normalizeAzimuth(azimuth - centerAzimuth);
  return delta > 180 ? delta - 360 : delta;
};

const projectSky = (azimuth: number, altitude: number, centerAzimuth: number) => {
  if (altitude < -2 || altitude > 88) return null;
  const delta = signedAzimuthDelta(azimuth, centerAzimuth);
  if (Math.abs(delta) > skyHorizontalFov / 2) return null;
  return {
    x: ((delta + skyHorizontalFov / 2) / skyHorizontalFov) * 100,
    y: 88 - (Math.max(0, altitude) / 88) * 78
  };
};

const projectCardinal = (label: string, azimuth: number, centerAzimuth: number) => {
  const delta = signedAzimuthDelta(azimuth, centerAzimuth);
  if (Math.abs(delta) > skyHorizontalFov / 2) return null;
  return {
    id: label,
    label,
    azimuth,
    x: Math.min(96, Math.max(4, ((delta + skyHorizontalFov / 2) / skyHorizontalFov) * 100)),
    y: Math.abs(delta) < 5 ? 82 : 87
  };
};

const horizonFromRaDec = (date: Date, ra: number, dec: number) => Horizon(date, tokyoObserver, ra, dec, "normal");

const projectBody = (
  date: Date,
  body: Body,
  name: string,
  kind: "moon" | "planet",
  mag: number,
  centerAzimuth: number
) => {
  const eq = Equator(body, date, tokyoObserver, true, true);
  const hor = horizonFromRaDec(date, eq.ra, eq.dec);
  const point = projectSky(hor.azimuth, hor.altitude, centerAzimuth);
  return point
    ? {
        id: name.toLowerCase(),
        name,
        kind,
        x: point.x,
        y: point.y,
        altitude: hor.altitude,
        azimuth: hor.azimuth,
        mag
      }
    : null;
};

export const buildSkyScene = (date: Date, centerAzimuth = 180): SkyScene => {
  const normalizedCenterAzimuth = normalizeAzimuth(centerAzimuth);
  const projectedStars = skyStars
    .map((star) => {
      const hor = horizonFromRaDec(date, star.ra, star.dec);
      const point = projectSky(hor.azimuth, hor.altitude, normalizedCenterAzimuth);
      return point
        ? {
            id: star.id,
            name: star.name,
            kind: "star" as const,
            constellation: star.constellation,
            x: point.x,
            y: point.y,
            altitude: hor.altitude,
            azimuth: hor.azimuth,
            mag: star.mag
          }
        : null;
    })
    .filter(Boolean) as ProjectedSkyObject[];

  const projectedById = new Map(projectedStars.map((star) => [star.id, star]));
  const projectedConstellations = constellationLines
    .map((line) => ({
      id: line.id,
      name: line.name,
      points: line.stars.map((id) => projectedById.get(id)).filter(Boolean) as ProjectedSkyObject[]
    }))
    .filter((line) => line.points.length >= 2)
    .map((line) => ({
      ...line,
      points: line.points.map(({ x, y }) => ({ x, y }))
    }));

  const bodies = [
    projectBody(date, Body.Moon, "Moon", "moon", -12, normalizedCenterAzimuth),
    projectBody(date, Body.Venus, "Venus", "planet", -4, normalizedCenterAzimuth),
    projectBody(date, Body.Mars, "Mars", "planet", -1, normalizedCenterAzimuth),
    projectBody(date, Body.Jupiter, "Jupiter", "planet", -2, normalizedCenterAzimuth),
    projectBody(date, Body.Saturn, "Saturn", "planet", 0, normalizedCenterAzimuth)
  ].filter(Boolean) as ProjectedSkyObject[];

  const cardinals = [
    projectCardinal("N", 0, normalizedCenterAzimuth),
    projectCardinal("E", 90, normalizedCenterAzimuth),
    projectCardinal("S", 180, normalizedCenterAzimuth),
    projectCardinal("W", 270, normalizedCenterAzimuth)
  ].filter(Boolean) as ProjectedCardinal[];

  const sunEq = Equator(Body.Sun, date, tokyoObserver, true, true);
  const sunHor = horizonFromRaDec(date, sunEq.ra, sunEq.dec);
  const phase = MoonPhase(date);

  return {
    date,
    stars: projectedStars,
    bodies,
    constellations: projectedConstellations,
    cardinals,
    moonPhase: phase,
    moonAge: (phase / 360) * synodicMonth,
    sunAltitude: sunHor.altitude,
    centerAzimuth: normalizedCenterAzimuth
  };
};

const searchEvent = (body: Body, direction: 1 | -1, dateValue: string) => {
  const start = new Date(`${dateValue}T00:00:00+09:00`);
  const found = SearchRiseSet(body, tokyoObserver, direction, start, 1.2);
  return found ? formatJstTime(found.date) : "--:--";
};

export const buildAstroSummary = (sample: Date): AstroSummary => {
  const dateValue = toDateInputValue(sample);
  const phase = MoonPhase(sample);
  const full = SearchMoonPhase(180, sample, 31);
  const nextNew = SearchMoonPhase(0, sample, 31);

  return {
    date: formatJstDate(sample),
    sunrise: searchEvent(Body.Sun, 1, dateValue),
    sunset: searchEvent(Body.Sun, -1, dateValue),
    moonrise: searchEvent(Body.Moon, 1, dateValue),
    moonset: searchEvent(Body.Moon, -1, dateValue),
    moonAge: `${((phase / 360) * synodicMonth).toFixed(1)}日`,
    daysToFull: full ? `${Math.max(0, (full.date.getTime() - sample.getTime()) / 86400000).toFixed(1)}日` : "--",
    daysToNew: nextNew ? `${Math.max(0, (nextNew.date.getTime() - sample.getTime()) / 86400000).toFixed(1)}日` : "--"
  };
};
